import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, getUser, getUsersByPortfolioCompany, createUser, updateUser } from '@lingaai/supabase-client';
import type { User } from '@lingaai/types';

export function useUsers(tenantId?: string) {
  return useQuery({
    queryKey: ['users', tenantId],
    queryFn: () => getUsers(tenantId),
  });
}

export function useUsersByPortfolioCompany(portfolioCompanyId: string) {
  return useQuery({
    queryKey: ['users', 'portfolioCompany', portfolioCompanyId],
    queryFn: () => getUsersByPortfolioCompany(portfolioCompanyId),
    enabled: !!portfolioCompanyId,
  });
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUser(userId),
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; tenant_id: string | null; portfolio_company_id: string | null; role: 'super_admin' | 'admin' | 'portco'; email: string; full_name: string | null }) =>
      createUser(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { userId: string; updates: Partial<User> }) => updateUser(variables.userId, variables.updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
    },
  });
}
